<template>
    <div class="example">
        <quill-editor
            class="editor"
            ref="myTextEditor"
            :instanceName="+new Date()"
            v-model="content"
            :options="editorOption"
            :disabled="disabled"
            @change="onEditorChange"
            @ready="onEditorReady($event)"
        />
        <input type="hidden"/>
    </div>
</template>

<script>
import dedent from "dedent";
import hljs from "highlight.js";
import { quillEditor } from "vue-quill-editor";
import Quill from "quill"; // Needed to register custom blots
import "highlight.js/styles/tomorrow.css";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";

// ==========================================
// 1. Define and Register the Page Break Blot
// ==========================================
const BlockEmbed = Quill.import('blots/block/embed');

class PageBreakBlot extends BlockEmbed {
    static create() {
        const node = super.create();
        node.setAttribute('class', 'ql-page-break');
        return node;
    }
}
PageBreakBlot.blotName = 'pagebreak';
PageBreakBlot.tagName = 'hr';

Quill.register(PageBreakBlot);
// ==========================================

// ==========================================
// 2. Define and Register the Variable Blot
// ==========================================
const Inline = Quill.import('blots/inline');

class VariableBlot extends Inline {
    static create(value) {
        let node = super.create();
        if (typeof value === 'string') {
            node.setAttribute('data-tag', value);
        }
        node.setAttribute('class', 'doc-variable');
        return node;
    }

    static formats(node) {
        return node.getAttribute('data-tag');
    }
}
VariableBlot.blotName = 'docVariable';
VariableBlot.tagName = 'w-span';
Quill.register(VariableBlot);

export default {
    props: ["value", "disabled", "variables"],
    components: {
        quillEditor,
    },
    data() {
        return {
            editorOption: {
                modules: {
                    toolbar: {
                        container: [
                            ["bold", "italic", "underline", "strike"],
                            ["blockquote", "code-block"],
                            [{ header: 1 }, { header: 2 }],
                            [{ list: "ordered" }, { list: "bullet" }],
                            [{ script: "sub" }, { script: "super" }],
                            [{ indent: "-1" }, { indent: "+1" }],
                            [{ direction: "rtl" }],
                            [{ size: ["small", false, "large", "huge"] }],
                            [{ header: [1, 2, 3, 4, 5, 6, false] }],
                            [{ font: [] }],
                            [{ color: [] }, { background: [] }],
                            [{ align: [] }],
                            ["clean"],
                            ["link", "image", "video"],
                            ["pagebreak"], // Added to toolbar
                        ],
                        handlers: {
                            // 2. Add Handler for the custom button
                            pagebreak: function () {
                                // 'this' refers to the toolbar module in standard Quill handlers
                                const quill = this.quill;
                                const range = quill.getSelection(true);
                                quill.insertEmbed(range.index, 'pagebreak', true);
                                quill.setSelection(range.index + 1);
                            }
                        }
                    },
                    syntax: {
                        highlight: (text) => hljs.highlightAuto(text).value,
                    },
                },
            },
            content: null,
        };
    },
    watch: {
        value(val) {
            if (val) {
                this.content = dedent`${val}`;
            } else {
                this.content = dedent`${""}`;
            }
        },
    },
    mounted() {},
    methods: {
        onEditorChange({ html }) {
            this.content = html;
            this.$emit("input", this.content);
            this.$emit("change", this.content);
        },
        onEditorReady() {
            if (this.value) {
                this.content = dedent`${this.value}`;
            }
        },
        addVariable(variable) {
            const range = this.editor.getSelection();
            if (range) {
                this.editor.insertText(range.index, variable, { bold: false });
            }
        },
    },
    computed: {
        editor() {
            return this.$refs.myTextEditor.quill;
        },
        quill() {
            return this.$refs.myTextEditor;
        },
        contentCode() {
            return hljs.highlightAuto(this.content).value;
        },
    },
};
</script>

<style>
/* 3. A4 and Print Styling */

/* Make the toolbar sticky */
.ql-toolbar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
    border-bottom: 1px solid #ccc !important;
}

/* Give the editor background a "desk" feel */
.example {
    background-color: #f0f2f5;
    display: flex;
    flex-direction: column;
}

/* Scrollable container for the "desk" */
.ql-container {
    flex: 1;
    overflow-y: auto;
    max-height: 70vh; /* Keeps it within view, adjust as needed */
    padding: 20px 0;
    border: none !important;
}

/* Force the editable area into an A4 aspect */
.ql-editor {
    background-color: white;
    width: 210mm;
    min-height: 297mm;
    margin: 0 auto;
    padding: 20mm; /* Standard A4 padding */
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
}

/* Icon for the toolbar button (Quill doesn't have one for pagebreak by default) */
.ql-pagebreak::before {
    content: "📄";
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Style the page break line inside the editor */
.ql-editor hr.ql-page-break {
    border: none;
    border-bottom: 2px dashed #0056b3;
    margin: 40px 0;
    position: relative;
    cursor: default;
    overflow: visible;
}

.ql-editor hr.ql-page-break::after {
    content: "PAGE BREAK";
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    padding: 0 10px;
    color: #0056b3;
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 1px;
}

/* How the page break should behave when printed or exported to PDF */

</style>